import React, {useCallback, useMemo} from 'react';
import cn from 'classnames';

import RefinementValueModel from 'coreClasses/search/RefinementValueModel';
import useGeolocalizedSizes from 'coreHooks/useGeolocalizedSizes';

import Accordion from 'brandComponents/Accordion';
import AccordionGroup, {AccordionGroupItem} from 'brandComponents/core/AccordionGroup';
import {EFiltersRefinement} from 'brandComponents/flyouts/FlyoutFilters/FiltersRefinement/IFiltersRefinement';
import {useRefinementsContext} from 'brandComponents/flyouts/FlyoutFilters/FlyoutFilterContext';
import {getRefinementComponent} from 'brandComponents/flyouts/FlyoutFilters/helpers';

import FiltersRefinementContent from '../../FiltersRefinementContent';

import {IFiltersRefinementContentItemProps} from './IFiltersRefinementContentItem';

import refinementsContentStyles from '../../FiltersRefinementContent.module.scss';
import itemStyles from './FiltersRefinementContentItem.module.scss';

const FiltersRefinementContentItem = ({
	refinementValue,
	style,
	classNames,
	split
}: IFiltersRefinementContentItemProps) => {
	const {setIsLoading} = useRefinementsContext();
	const refinements = refinementValue.getValues();
	const children = refinementValue.getRefinementModel().getChildren();
	const refinementModel = useMemo(() => children.find((child) => {
		return child.getLabel() === refinementValue.getLabel();
	}), [children]);
	const currentCgid = refinementValue.getRefinementModel().getSearchModel().getSelectedRefinements().cgid;
	const localizedSizes = useGeolocalizedSizes(
		null,
		currentCgid
			? {getByCategory: true, cgid: currentCgid}
			: {getBySize: true, EUSize: refinementValue.getLabel().toLowerCase()}
	);

	const handleChange = useCallback((e: React.SyntheticEvent, refValue: RefinementValueModel) => {
		const target = e.target as HTMLFormElement;

		refValue.toggle(target.checked);
		setIsLoading(true);

		refValue
			.getSearchProvider()
			.search()
			.finally(() => setIsLoading(false));
	}, []);

	return (
		<>
			{refinements.length === 0 ?
				<li
					style={style}
					key={refinementValue.getKey()}
					className={itemStyles['filters-refinement__item-simple']}
				>
					<label
						htmlFor={refinementValue.getKey()}
						className={cn(itemStyles['filters-refinement__label'], classNames?.label, {
							active: refinementValue.isActive()
						})}
					>
						<span className={itemStyles['filters-refinement__label--text']}>
							{(
								refinementValue.getRefinementModel().getAttributeId() === EFiltersRefinement.SIZE &&
								localizedSizes[refinementValue.getLabel()]
							)
								? localizedSizes[refinementValue.getLabel()]
								: split
									? refinementValue.getLabel().split('-')[1].toLowerCase()
									: refinementValue.getLabel().toLowerCase()
							}
						</span>
						<div className={cn(itemStyles['filters-refinement__field'], 'field')}>
							<input
								id={refinementValue.getKey()}
								className={cn(itemStyles['filters-refinement__checkbox'], classNames?.input)}
								type="checkbox"
								onChange={(e) => handleChange(e, refinementValue)}
								checked={refinementValue.isActive()}
							/>
						</div>
					</label>
				</li>
				:
				<Accordion
					key={refinementValue.getKey()}
					classNames={{accordion: cn(refinementsContentStyles['filters-refinement__item-accordion'])}}
					titleContent={refinementValue.getLabel().toLowerCase()}
				>
					<AccordionGroup>
						{
							refinementModel.getValues().map((refValue) => {
								const refMod = refinementModel.getChildren().find((child) => {
									return child.getLabel() === refValue.getLabel();
								});

								if (refMod) {
									const attributeId = refMod.getAttributeId();
									const Component = getRefinementComponent(attributeId);

									return (
										<AccordionGroupItem
											key={refMod.getKey()}
											title={refMod.getLabel().toLowerCase()}
											classNames={cn(itemStyles['filters-refinement__item-has-sublist'])}
											content={
												<FiltersRefinementContent key={refMod.getAttributeId()}>
													<Component refinement={refMod}/>
												</FiltersRefinementContent>
											}
										/>
									);
								} else {
									return (

										<li
											key={refValue.getKey()}
											className={cn(itemStyles['filters-refinement-sub__item'])}
										>
											<label
												htmlFor={refValue.getKey()}
												className={cn(itemStyles['filters-refinement-sub__label'], {
													active: refValue.isActive()
												})}
											>
												<input
													id={refValue.getKey()}
													type="checkbox"
													onChange={(e) => handleChange(e, refValue)}
													className={cn(itemStyles['filters-refinement-sub__input'])}
												/>
												<span
													className={cn(itemStyles['filters-refinement-sub__checkbox'])}
												></span>
												{refValue.getLabel().toLowerCase()}
											</label>
										</li>
									);
								}
							})
						}
					</AccordionGroup>
				</Accordion>
			}
		</>
	);
};

export default FiltersRefinementContentItem;
